/**
 * Dashboard - FR-8.2 (AC3).
 *
 * Four things, in the order an admin works: what is waiting, then what members
 * are actually contacting.
 *
 * Every number is served by ONE call (`GET /admin/dashboard`) and each one is the
 * `total` of the screen it links to, computed server-side from that screen's own
 * predicate - so a tile and its detail page cannot disagree. Nothing on this
 * screen is derived in the browser.
 *
 * The vendors widget renders spec 11's `metric_label` and `metric_note` verbatim.
 * FR-6.3.3 fixes those words and forbids "usage", "orders" and "redemptions" for
 * this metric, and the spec 11 handoff entry requires spec 12 to render the
 * server's wording rather than paraphrase it.
 */

import { Link } from "react-router-dom";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Card,
  CardHeader,
  ErrorNotice,
  EmptyState,
  LiveBadge,
  LoadingRows,
  Page,
} from "../components/primitives";
import { fetchDashboard, type Dashboard } from "../lib/api/dashboard";
import type { LeaderboardEntry } from "../lib/api/referrals";
import { useQuery } from "../lib/hooks";
import { formatCount } from "../lib/format";

export function DashboardScreen() {
  const query = useQuery<Dashboard>(() => fetchDashboard(), []);
  const { data, error, loading, reload } = query;

  if (loading && !data) {
    return (
      <Page>
        <LoadingRows rows={2} columns={3} />
      </Page>
    );
  }

  if (error && !data) {
    return (
      <Page>
        <ErrorNotice error={error} onRetry={reload} />
      </Page>
    );
  }

  if (!data) return null;

  const vendors = data.top_vendors;

  return (
    <Page>
      {/* The two queues first: this is the work, and the nav badges the same
          two numbers. */}
      <div className="tiles">
        <Link to="/registrations" style={{ display: "block" }}>
          <button className="tile" type="button" style={{ width: "100%" }}>
            <span className="label">Pending approvals</span>
            <span className="tile__value tile__value--waiting figure">
              {formatCount(data.pending_users)}
            </span>
            <span className="tile__foot">
              {data.pending_users === 0
                ? "Nothing waiting."
                : data.pending_users === 1
                  ? "1 registration to review."
                  : `${formatCount(data.pending_users)} registrations to review.`}
            </span>
          </button>
        </Link>

        <Link to="/family-biz?queue=true" style={{ display: "block" }}>
          <button className="tile" type="button" style={{ width: "100%" }}>
            <span className="label">FamilyBiz review</span>
            <span className="tile__value tile__value--waiting figure">
              {formatCount(data.pending_family_biz)}
            </span>
            <span className="tile__foot">
              {data.pending_family_biz === 0
                ? "Nothing waiting."
                : "Submissions and owner requests."}
            </span>
          </button>
        </Link>

        <Link to="/members?status=active" style={{ display: "block" }}>
          <button className="tile" type="button" style={{ width: "100%" }}>
            <span className="label">Active members</span>
            <span className="tile__value figure">{formatCount(data.active_members)}</span>
            <span className="tile__foot">Accounts with `active` status.</span>
          </button>
        </Link>
      </div>

      <Card flush>
        <div style={{ padding: "24px 24px 0" }}>
          <CardHeader
            title="Top vendors by contact volume"
            hint={
              <>
                {vendors.metric_label} over the last {data.top_vendors_days} days
                {vendors.from_date && vendors.to_date
                  ? ` (${vendors.from_date} to ${vendors.to_date})`
                  : ""}
                . {vendors.metric_note}
              </>
            }
            actions={
              <Link to="/referrals" className="btn btn--ghost btn--sm">
                Full report
              </Link>
            }
          />
        </div>

        {vendors.items.length === 0 ? (
          <EmptyState
            title="No partner contacts in this period."
            // The server's own empty copy for this metric, not a second sentence
            // written here.
            body={vendors.message ?? undefined}
            action={
              <Link to="/referrals" className="btn btn--ghost btn--sm">
                Open the reports
              </Link>
            }
          />
        ) : (
          <DataTable
            rows={vendors.items}
            rowKey={(row) => row.target_id}
            caption={`${vendors.metric_label}, ranked. ${vendors.metric_note}`}
            columns={topVendorColumns(vendors.metric_label)}
          />
        )}
      </Card>
    </Page>
  );
}

function topVendorColumns(metricLabel: string): Array<Column<LeaderboardEntry>> {
  return [
    {
      key: "rank",
      header: "#",
      align: "right",
      numeric: true,
      render: (row) => row.rank,
    },
    {
      key: "name",
      header: "Partner",
      render: (row) => (
        <PrimaryCell
          title={row.name}
          detail={row.is_listed ? undefined : "Not in the member directory"}
        />
      ),
    },
    {
      key: "listed",
      header: "Visibility",
      render: (row) => (row.is_listed ? <LiveBadge /> : <span className="text-meta">-</span>),
    },
    {
      key: "referrals",
      header: metricLabel,
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.referrals_initiated),
    },
    {
      key: "members",
      header: "Distinct members",
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.distinct_members),
    },
    {
      key: "open",
      header: "",
      align: "right",
      render: (row) => (
        <Link to={`/referrals?vendor_id=${row.target_id}`} className="btn btn--ghost btn--sm">
          Report
        </Link>
      ),
    },
  ];
}
